import React, { useEffect, useState, useRef, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { formatCustomDateTime } from "../../../../utils/dateUtils";
import { showSuccessAlert, showErrorAlert, showDeleteConfirmationAlert } from "../../../../utils/alertUtils";
import { all_routes } from "../../../router/all_routes";
import axios from "axios";
import { API_BASE_URL } from "../../../../environment";
import { getAuthHeaders } from "../../../../utils/authUtils";
import TaskDetailModal from "../../../../components/TaskDetailModal";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekYear from "dayjs/plugin/weekYear";
import classNames from "classnames";
import { Calendar as BigCalendar, dayjsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar.css";
import CreatableSelect from "react-select/creatable";
import { AuthContext } from "../../../../context/authcontext";
import {
    CalendarService,
    CalendarEventType,
    LinkedCalendar,
    CalendarEvent,
    CalendarTask,
    EventMeeting,
    EventAttendee,
    EventAttachment,
} from "./calendar.service";

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(weekYear);

const localizer = dayjsLocalizer(dayjs);

type ViewMode = "week" | "day" | "month" | "work_week";

interface ICalendarItem {
    id: string | number;
    title: string;
    start: Date;
    end: Date;
    itemType: "event" | "task";
    backgroundColor?: string;
    foregroundColor?: string;
    calendarEventId?: number;
    calendarTaskId?: number;
    isAllDay?: boolean;
    description?: string;
    location?: string;
    status?: string;
    eventTypeId?: number;
    linkedCalendarId?: number;
    timezone?: string;
    htmlLink?: string;
    isCompleted?: boolean;
    priority?: string;
    taskGroupName?: string;
    parentTaskId?: string | number | null;
    notes?: string;
    calendarTaskGroupId?: number | string;
    calendarName?: string;
    attachments?: EventAttachment[];
    isRecursive?: boolean;
    taskSource?: string;
    relatedTaskId?: number | null;
    relatedEntityId?: number | null;
    relatedEntityName?: string | null;
    directCrmTaskId?: number | null;
}

const getUnitType = (viewMode: ViewMode) => {
    switch (viewMode) {
        case "week":
        case "work_week":
            return "week" as const;
        case "month":
            return "month" as const;
        default:
            return "day" as const;
    }
};

const getLabel = (date: Date, viewMode: ViewMode): string => {
    if (viewMode === "month") {
        return `${dayjs(date).format("MMMM")} ${dayjs(date).format("YYYY")}`;
    }
    if (viewMode === "week") {
        return `${dayjs(date).startOf("week").format("MMM D")} - ${dayjs(date).endOf("week").format("MMM D")}`;
    }
    if (viewMode === "work_week") {
        return `${dayjs(date).startOf("week").add(1, "day").format("MMM D")} - ${dayjs(date).endOf("week").add(-1, "day").format("MMM D")}`;
    }
    return `${dayjs(date).format("dddd")}, ${dayjs(date).format("MMM D")}`;
};

const getTodayButtonLabel = (viewMode: ViewMode): string => {
    if (viewMode === "month") return "This month";
    if (viewMode === "week" || viewMode === "work_week") return "This week";
    return "Today";
};

const getDateRange = (date: Date, viewMode: ViewMode): { start: string; end: string } => {
    const d = dayjs(date);
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs;

    switch (viewMode) {
        case "month":
            start = d.startOf("month").startOf("week");
            end = d.endOf("month").endOf("week");
            break;
        case "week":
            start = d.startOf("week");
            end = d.endOf("week");
            break;
        case "work_week":
            start = d.startOf("week").add(1, "day");
            end = d.startOf("week").add(5, "day").endOf("day");
            break;
        default:
            start = d.startOf("day");
            end = d.endOf("day");
            break;
    }

    return {
        start: start.toISOString(),
        end: end.toISOString(),
    };
};

const PROVIDER_CONFIG: Record<string, { icon: string; iconColor: string }> = {
    GOOGLE: { icon: "ti ti-brand-google", iconColor: "#EA4335" },
    OUTLOOK: { icon: "ti ti-brand-windows", iconColor: "#0078D4" },
    APPLE: { icon: "ti ti-brand-apple", iconColor: "#333" },
};

const GOOGLE_COLOR_MAP: Record<string, string> = {
    '#a4bdfc': '#7986CB',
    '#7ae7bf': '#33B679',
    '#dbadff': '#8E24AA',
    '#ff887c': '#E67C73',
    '#fbd75b': '#F6BF26',
    '#ffb878': '#F4511E',
    '#46d6db': '#039BE5',
    '#e1e1e1': '#616161',
    '#5484ed': '#3F51B5',
    '#51b749': '#0B8043',
    '#dc2127': '#D50000',
    '#039be5': '#039BE5',
    '#ac725e': '#A05A2C',
    '#d06b64': '#E67C73',
    '#f83a22': '#D50000',
    '#fa573c': '#E67C73',
    '#ff7537': '#F4511E',
    '#ffad46': '#F6BF26',
    '#42d692': '#33B679',
    '#16a765': '#0B8043',
    '#7bd148': '#7CB342',
    '#b3dc6c': '#C0CA33',
    '#fbe983': '#FBC02D',
    '#fad165': '#F6BF26',
    '#92e1c0': '#42B6AF',
    '#9fe1e7': '#039BE5',
    '#9fc6e7': '#7986CB',
    '#4986e7': '#3F51B5',
    '#9a9cff': '#7986CB',
    '#b99aff': '#8E24AA',
    '#c2c2c2': '#616161',
    '#cabdbf': '#D0D0D0',
    '#cca6ac': '#AD1457',
    '#f691b2': '#F06292',
    '#cd74e6': '#8E24AA',
    '#a47ae2': '#7986CB',
};

const MODERN_PALETTE = [
    '#7986CB', '#33B679', '#8E24AA', '#E67C73', '#F6BF26',
    '#F4511E', '#039BE5', '#616161', '#3F51B5', '#0B8043', '#D50000'
];

const getModernColor = (hex: string) => {
    if (!hex) return hex;
    return GOOGLE_COLOR_MAP[hex.toLowerCase()] || hex;
};

const UserAvatar: React.FC<{ name: string; size?: number; className?: string }> = ({ name, size = 32, className }) => {
    const initials = name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

    const colors = ["#6366f1", "#8b5cf6", "#a855f7", "#ec4899", "#f43f5e", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#06b6d4"];
    const colorIndex = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;

    return (
        <div
            className={classNames("d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold", className)}
            style={{
                width: size,
                height: size,
                fontSize: size * 0.38,
                backgroundColor: colors[colorIndex],
                flexShrink: 0,
            }}
        >
            {initials}
        </div>
    );
};

const EventComponent = ({ event }: { event: ICalendarItem }): JSX.Element => {
    const isTask = event.itemType === "task";
    const start = dayjs(event.start);
    const end = dayjs(event.end);
    const durationMinutes = end.diff(start, "minute");
    const showTime = durationMinutes > 30 && !event.isAllDay;

    const samePeriod = start.format("a") === end.format("a");
    const startTimeStr = start.format(start.minute() === 0 ? "h" : "h:mm") + (samePeriod ? "" : start.format("a"));
    const endTimeStr = end.format(end.minute() === 0 ? "h" : "h:mm") + end.format("a");
    const timeString = `${startTimeStr} - ${endTimeStr}`;

    return (
        <div
            className={classNames("d-flex w-100 h-100", {
                "calendar-task-item": isTask,
                "flex-column align-items-start justify-content-start": showTime,
                "align-items-center": !showTime
            })}
            title={`${event.title}\n${formatCustomDateTime(event.start.toISOString())} - ${formatCustomDateTime(event.end.toISOString())}`}
            style={{ cursor: "pointer", minHeight: "100%", position: "relative", overflow: "hidden" }}
        >
            <div className="d-flex align-items-center w-100" style={{ minWidth: 0 }}>
                {isTask && event.calendarTaskGroupId && (
                    <i
                        className={classNames("ti me-1", event.isCompleted ? "ti-circle-check" : "ti-circle")}
                        style={{ fontSize: "0.75rem", opacity: 0.8, flexShrink: 0 }}
                    />
                )}
                {isTask && event.taskSource === "calendar" && (
                    <span style={{ fontSize: "0.6rem", fontWeight: "bold", background: "rgba(68, 124, 228, 0.2)", color: "#fff", padding: "1px 4px", borderRadius: "3px", marginRight: "4px", flexShrink: 0, lineHeight: 1 }}>GT</span>
                )}
                {isTask && event.taskSource === "crm" && (
                    <span style={{ fontSize: "0.6rem", fontWeight: "bold", background: "rgba(255,255,255,0.2)", color: "#fff", padding: "1px 4px", borderRadius: "3px", marginRight: "4px", flexShrink: 0, lineHeight: 1 }}>CRM</span>
                )}
                {!isTask && event.relatedEntityName === "task" && (
                    <span style={{ fontSize: "0.6rem", fontWeight: "bold", background: "rgba(255,255,255,0.2)", color: "#fff", padding: "1px 4px", borderRadius: "3px", marginRight: "4px", flexShrink: 0, lineHeight: 1 }}>TASK</span>
                )}
                <span style={{
                    color: "white",
                    fontSize: "0.78rem",
                    fontWeight: "bold",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    width: "100%"
                }}>
                    {event.title}
                    {event.isRecursive && (
                        <i className="ti ti-refresh ms-1" style={{ fontSize: "0.7rem", opacity: 0.9 }} title="Recursive Event" />
                    )}
                </span>
            </div>
            {showTime && (
                <span style={{
                    color: "white",
                    fontSize: "0.72rem",
                    opacity: 0.95,
                    lineHeight: 1.2,
                    marginTop: "1px",
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                }}>
                    {timeString}
                </span>
            )}
        </div>
    );
};

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const MiniCalendar: React.FC<{ selectedDate: Date; onDateSelect: (date: Date) => void }> = ({ selectedDate, onDateSelect }) => {
    const [miniMonth, setMiniMonth] = useState(() => dayjs(selectedDate).startOf("month"));

    const today = dayjs();
    const selectedDay = dayjs(selectedDate);

    const startOfMonth = miniMonth.startOf("month");
    const daysInMonth = miniMonth.daysInMonth();
    const startDayOfWeek = startOfMonth.day();

    const totalCells = Math.ceil((startDayOfWeek + daysInMonth) / 7) * 7;
    const cells: (dayjs.Dayjs | null)[] = [];
    for (let i = 0; i < totalCells; i++) {
        const dayIndex = i - startDayOfWeek;
        if (dayIndex < 0 || dayIndex >= daysInMonth) {
            cells.push(null);
        } else {
            cells.push(startOfMonth.add(dayIndex, "day"));
        }
    }

    return (
        <div className="mini-calendar">
            <div className="mini-cal-header">
                <button className="mini-cal-nav" onClick={() => setMiniMonth(miniMonth.subtract(1, "month"))}>
                    <i className="ti ti-chevron-left" />
                </button>
                <div className="mini-cal-selectors">
                    <select
                        className="mini-cal-select month-select"
                        value={miniMonth.month()}
                        onChange={(e) => setMiniMonth(miniMonth.month(parseInt(e.target.value)))}
                    >
                        {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>
                                {dayjs().month(i).format("MMMM")}
                            </option>
                        ))}
                    </select>
                    <select
                        className="mini-cal-select year-select"
                        value={miniMonth.year()}
                        onChange={(e) => setMiniMonth(miniMonth.year(parseInt(e.target.value)))}
                    >
                        {Array.from({ length: 21 }, (_, i) => (
                            <option key={i} value={dayjs().year() - 10 + i}>
                                {dayjs().year() - 10 + i}
                            </option>
                        ))}
                    </select>
                </div>
                <button className="mini-cal-nav" onClick={() => setMiniMonth(miniMonth.add(1, "month"))}>
                    <i className="ti ti-chevron-right" />
                </button>
            </div>
            <div className="mini-cal-grid">
                {WEEK_DAYS.map((d) => (
                    <div key={d} className="mini-cal-weekday">{d}</div>
                ))}
                {cells.map((cell, idx) => {
                    if (!cell) return <div key={`empty-${idx}`} className="mini-cal-day empty" />;
                    const isToday = cell.isSame(today, "day");
                    const isSelected = cell.isSame(selectedDay, "day");
                    return (
                        <button
                            key={cell.format("YYYY-MM-DD")}
                            className={classNames("mini-cal-day", { today: isToday, selected: isSelected })}
                            onClick={() => onDateSelect(cell.toDate())}
                        >
                            {cell.date()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const getMeetingIcon = (provider: string): string => {
    const p = provider.toLowerCase();
    if (p.includes("google") || p.includes("meet")) return "ti ti-brand-google";
    if (p.includes("zoom")) return "ti ti-brand-zoom";
    if (p.includes("teams") || p.includes("microsoft")) return "ti ti-brand-teams";
    return "ti ti-video";
};

const getMeetingIconColor = (provider: string): string => {
    const p = provider.toLowerCase();
    if (p.includes("google") || p.includes("meet")) return "#EA4335";
    if (p.includes("zoom")) return "#2D8CFF";
    if (p.includes("teams") || p.includes("microsoft")) return "#6264A7";
    return "#6366f1";
};

const getStatusBadge = (status: string) => {
    const s = status.toLowerCase();
    if (s === "accepted") return { color: "#22c55e", bg: "rgba(34,197,94,0.1)", label: "Accepted" };
    if (s === "declined") return { color: "#ef4444", bg: "rgba(239,68,68,0.1)", label: "Declined" };
    if (s === "tentative") return { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", label: "Tentative" };
    if (s === "needsaction") return { color: "#ef4444", bg: "rgba(239,68,68,0.1)", label: "Needs Action" };
    return { color: "#003b8fff", bg: "rgba(148,163,184,0.1)", label: status };
};

const CalendarPage: React.FC = () => {
    const navigate = useNavigate();
    const { selectedTenantId, auth } = useContext(AuthContext);
    const userId = auth?.user_id;

    const [eventTypes, setEventTypes] = useState<CalendarEventType[]>([]);
    const [linkedCalendars, setLinkedCalendars] = useState<LinkedCalendar[]>([]);
    const [apiEvents, setApiEvents] = useState<CalendarEvent[]>([]);
    const [apiTasks, setApiTasks] = useState<CalendarTask[]>([]);

    const [enabledEventTypes, setEnabledEventTypes] = useState<Record<number, boolean>>({});
    const [tasksEnabled, setTasksEnabled] = useState(true);
    const [enabledCalendars, setEnabledCalendars] = useState<Record<number, boolean>>({});
    const [expandedProviders, setExpandedProviders] = useState<Record<string, boolean>>({});

    const [viewMode, setViewMode] = useState<ViewMode>("work_week");
    const [date, setDate] = useState<Date>(new Date());
    const [viewModeDropdownOpen, setViewModeDropdownOpen] = useState(false);
    const viewModeDropdownRef = useRef<HTMLDivElement>(null);

    const [selectedEvent, setSelectedEvent] = useState<ICalendarItem | null>(null);
    const [selectedTask, setSelectedTask] = useState<ICalendarItem | null>(null);
    const [eventMeetings, setEventMeetings] = useState<EventMeeting[]>([]);
    const [eventAttendees, setEventAttendees] = useState<EventAttendee[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<EventAttachment[]>([]);
    const [createMeeting, setCreateMeeting] = useState(false);
    const [attendees, setAttendees] = useState<string[]>([]);
    const [attendeeInput, setAttendeeInput] = useState("");
    const [colorCategories, setColorCategories] = useState<any[]>([]);
    const [selectedColorId, setSelectedColorId] = useState<number | string>("");
    const [summary, setSummary] = useState("");
    const [description, setDescription] = useState("");
    const [location, setLocation] = useState("");
    const [selectedCalendarId, setSelectedCalendarId] = useState<number | string>("");
    const [isEditing, setIsEditing] = useState(false);
    const [relatedEvents, setRelatedEvents] = useState<CalendarEvent[]>([]);
    const [showRelatedEventsModal, setShowRelatedEventsModal] = useState(false);
    const [relatedEventsLoading, setRelatedEventsLoading] = useState(false);
    const [relatedEventTitle, setRelatedEventTitle] = useState("");
    const [relatedEventColor, setRelatedEventColor] = useState("");

    const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
    const [taskDetails, setTaskDetails] = useState<any>(null);
    const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);
    const [isEditingTask, setIsEditingTask] = useState(false);

    const handleOpenCRMTask = useCallback(async (taskId: number) => {
        setSelectedTask(null);
        setIsTaskModalVisible(true);
        setIsLoadingTaskDetails(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/task/${taskId}/`, {
                headers: getAuthHeaders(),
            });
            setTaskDetails(response.data);
        } catch (err) {
            console.error("Error loading task details:", err);
        } finally {
            setIsLoadingTaskDetails(false);
        }
    }, [selectedTenantId]);

    useEffect(() => {
        (window as any).openCRMTask = handleOpenCRMTask;
        return () => {
            delete (window as any).openCRMTask;
        };
    }, [handleOpenCRMTask]);

    const [loading, setLoading] = useState(false);
    const [linkedCalendarsLoaded, setLinkedCalendarsLoaded] = useState(false);
    const [crmTasks, setCrmTasks] = useState<any[]>([]);

    const [toggleInfoEventCanvas, setToggleInfoEventCanvas] = useState(false);
    const [canvasEvent, setCanvasEvent] = useState<ICalendarItem | null>(null);

    const startInputRef = useRef<HTMLInputElement>(null);
    const endInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (viewModeDropdownRef.current && !viewModeDropdownRef.current.contains(e.target as Node)) {
                setViewModeDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!selectedTenantId) return;
        CalendarService.fetchEventTypes(selectedTenantId).then((types) => {
            setEventTypes(types);
            const init: Record<number, boolean> = {};
            types.forEach((t) => (init[t.calendar_event_type_id] = true));
            setEnabledEventTypes(init);
        }).catch(() => { });
    }, [selectedTenantId]);

    useEffect(() => {
        if (!selectedTenantId || !userId) return;
        CalendarService.fetchLinkedCalendars(selectedTenantId, userId).then((cals) => {
            setLinkedCalendars(cals);
            const init: Record<number, boolean> = {};
            cals.forEach((c) => (init[c.calendar_auth_linked_calendar_id] = true));
            setEnabledCalendars(init);

            const provInit: Record<string, boolean> = {};
            cals.forEach((c) => {
                const key = c.calendar_provider?.calendar_provider_key;
                if (key && provInit[key] === undefined) provInit[key] = false;
            });
            setExpandedProviders(provInit);
        }).catch(() => { }).finally(() => setLinkedCalendarsLoaded(true));
    }, [selectedTenantId, userId]);

    useEffect(() => {
        if (selectedTenantId) {
            CalendarService.fetchColorCategories(selectedTenantId).then(setColorCategories).catch(console.error);
        }
    }, [selectedTenantId]);

    const fetchCalendarEvents = useCallback(() => {
        if (!selectedTenantId || !userId || !linkedCalendarsLoaded || linkedCalendars.length === 0) return;
        const { start, end } = getDateRange(date, viewMode);
        setLoading(true);
        CalendarService.fetchEvents(selectedTenantId, userId, start, end)
            .then(setApiEvents)
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [selectedTenantId, userId, date, viewMode, linkedCalendarsLoaded, linkedCalendars.length]);

    useEffect(() => {
        fetchCalendarEvents();
    }, [fetchCalendarEvents]);

    const fetchTasks = useCallback(() => {
        if (!selectedTenantId || !linkedCalendarsLoaded || linkedCalendars.length === 0) return;
        CalendarService.fetchTasks(selectedTenantId).then(setApiTasks).catch(() => { });
    }, [selectedTenantId, linkedCalendarsLoaded, linkedCalendars.length]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const fetchCrmTasks = useCallback(() => {
        if (!selectedTenantId || !linkedCalendarsLoaded || linkedCalendars.length > 0) return;
        axios.get(`${API_BASE_URL}/task/?tenant_id=${selectedTenantId}`, {
            headers: getAuthHeaders(),
        }).then((res) => {
            setCrmTasks(res.data.results || []);
        }).catch(() => { });
    }, [selectedTenantId, linkedCalendarsLoaded, linkedCalendars.length]);

    useEffect(() => {
        fetchCrmTasks();
    }, [fetchCrmTasks]);

    const calendarsByProvider = useMemo(() => {
        const grouped: Record<string, { key: string; displayName: string; calendars: LinkedCalendar[] }> = {};
        linkedCalendars.forEach((cal) => {
            const provKey = cal.calendar_provider?.calendar_provider_key || "OTHER";
            const provName = cal.calendar_provider?.calendar_provider_display_name || "Other";
            if (!grouped[provKey]) {
                grouped[provKey] = { key: provKey, displayName: provName, calendars: [] };
            }
            grouped[provKey].calendars.push(cal);
        });
        return Object.values(grouped);
    }, [linkedCalendars]);

    const calendarItems: ICalendarItem[] = useMemo(() => {
        const items: ICalendarItem[] = [];

        if (linkedCalendars.length > 0) {
            apiEvents.forEach((ev) => {
                if (!enabledEventTypes[ev.calendar_event_type_id]) return;
                if (!enabledCalendars[ev.calendar_auth_linked_calendar_id]) return;

                items.push({
                    id: `event-${ev.calendar_event_id}`,
                    title: ev.title,
                    start: new Date(ev.start_datetime),
                    end: new Date(ev.end_datetime),
                    itemType: "event",
                    backgroundColor: getModernColor(ev.calendar_color_category?.background_color_code || "#039BE5"),
                    foregroundColor: ev.calendar_color_category?.foreground_color_code || "#FFFFFF",
                    calendarEventId: ev.calendar_event_id,
                    isAllDay: ev.is_all_day,
                    description: ev.description,
                    location: ev.location,
                    status: ev.status,
                    eventTypeId: ev.calendar_event_type_id,
                    linkedCalendarId: ev.calendar_auth_linked_calendar_id,
                    timezone: ev.timezone,
                    htmlLink: ev.html_link,
                    calendarName: ev.calendar_auth_linked_calendar?.name,
                    attachments: ev.attachments,
                    isRecursive: ev.is_recursive,
                    relatedEntityId: ev.related_entity_id,
                    relatedEntityName: ev.related_entity_name,
                });
            });

            if (tasksEnabled) {
                apiTasks.forEach((task) => {
                    const dueDate = task.due_datetime
                        ? new Date(task.due_datetime)
                        : new Date(task.created_datetime);

                    items.push({
                        id: `task-${task.calendar_task_id}`,
                        title: task.title,
                        start: dueDate,
                        end: dayjs(dueDate).add(30, "minute").toDate(),
                        itemType: "task",
                        backgroundColor: task.is_completed ? "#22c55e" : "#f59e0b",
                        foregroundColor: "#FFFFFF",
                        calendarTaskId: task.calendar_task_id,
                        calendarTaskGroupId: task.calendar_task_group_id,
                        isCompleted: task.is_completed,
                        priority: task.priority,
                        taskGroupName: task.calendar_task_group?.task_group_name,
                        parentTaskId: task.parent_task_id,
                        notes: task.notes,
                        status: task.status,
                        description: task.notes,
                        taskSource: task.task_source,
                        relatedTaskId: task.related_task_id,
                    });
                });
            }
        } else if (linkedCalendarsLoaded && tasksEnabled) {
            crmTasks.forEach((task) => {
                const startDate = task.start_date ? new Date(task.start_date) : new Date(task.due_date || task.created_datetime);
                const endDate = task.due_date ? new Date(task.due_date) : dayjs(startDate).add(30, "minute").toDate();

                items.push({
                    id: `crm-direct-${task.task_id}`,
                    title: task.title,
                    start: startDate,
                    end: endDate,
                    itemType: "task",
                    backgroundColor: task.priority === "high" ? "#ef4444" : task.priority === "medium" ? "#f59e0b" : "#6366f1",
                    foregroundColor: "#FFFFFF",
                    description: task.description,
                    status: task.status,
                    priority: task.priority,
                    relatedEntityName: task.related_entity_name,
                    relatedEntityId: task.related_entity_id,
                    taskSource: "crm",
                    directCrmTaskId: task.task_id,
                });
            });
        }

        return items;
    }, [linkedCalendars, linkedCalendarsLoaded, apiEvents, apiTasks, crmTasks, enabledEventTypes, enabledCalendars, tasksEnabled]);

    const eventStyleGetter = (event: ICalendarItem) => {
        const bg = event.backgroundColor || "#039BE5";
        const fg = event.foregroundColor || "#FFFFFF";
        const now = new Date();
        const isPast = event.end < now;

        return {
            style: {
                backgroundColor: bg,
                color: fg,
                borderRadius: "8px",
                border: "none",
                fontSize: "0.8rem",
                opacity: isPast ? 0.6 : 1,
            },
        };
    };

    const onSelectEvent = async (event: ICalendarItem) => {
        if (event.itemType === "task") {
            if (event.directCrmTaskId) {
                handleOpenCRMTask(event.directCrmTaskId);
                return;
            }
            const task = apiTasks.find((t) => t.calendar_task_id === event.calendarTaskId);
            if (task) {
                setSelectedTask(event);
            }
            return;
        }

        setSelectedEvent(event);
        if (!selectedTenantId || !event.calendarEventId) return;

        setDetailLoading(true);
        try {
            const [meetings, attendees] = await Promise.all([
                CalendarService.fetchEventMeetings(selectedTenantId, event.calendarEventId),
                CalendarService.fetchEventAttendees(selectedTenantId, event.calendarEventId),
            ]);
            setEventMeetings(meetings);
            setEventAttendees(attendees);
        } catch {
            setEventMeetings([]);
            setEventAttendees([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleViewMode = (e: any): void => {
        setDate(dayjs(e).toDate());
        setViewMode("day");
    };

    const handleSlotSelect = (slotInfo: any): void => {
        if (viewMode === "month") return;
        const { start, end } = slotInfo;
        setCanvasEvent({
            id: "",
            title: "",
            start,
            end,
            itemType: "event",
        });
        setSummary("");
        setDescription("");
        setLocation("");
        const primaryCal = linkedCalendars.find(c => c.is_primary);
        setSelectedCalendarId(primaryCal?.calendar_auth_linked_calendar_id || "");
        setAttachments([]);
        setIsEditing(false);
        setToggleInfoEventCanvas(true);
    };

    const handleCreateEvent = async () => {
        if (!selectedCalendarId || !summary) {
            showErrorAlert("Please select a calendar and enter an event name.");
            return;
        }

        setIsSaving(true);
        try {
            const formData = new FormData();
            formData.append("calendar_id", String(selectedCalendarId));
            formData.append("summary", summary);
            formData.append("description", description);
            formData.append("location", location);
            formData.append("start", dayjs(canvasEvent?.start).format("YYYY-MM-DDTHH:mm:ss"));
            formData.append("end", dayjs(canvasEvent?.end).format("YYYY-MM-DDTHH:mm:ss"));
            formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
            formData.append("create_meeting", String(createMeeting));
            if (attendees.length > 0) {
                formData.append("attendees", JSON.stringify(attendees));
            }
            if (selectedColorId) {
                formData.append("color_id", String(selectedColorId));
            }

            const existingFiles = await Promise.all(
                existingAttachments.map(async (att) => {
                    const response = await fetch(att.attachment_url);
                    const blob = await response.blob();
                    return new File([blob], att.title, { type: blob.type || "application/octet-stream" });
                })
            );

            attachments.forEach((file) => {
                formData.append("attachments", file);
            });

            existingFiles.forEach((file) => {
                formData.append("attachments", file);
            });

            await CalendarService.createEvent(formData);
            showSuccessAlert("Event created successfully");
            fetchCalendarEvents();
            closeCanvas();
        } catch (error: any) {
            showErrorAlert(error.response?.data?.error || "Failed to create event");
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateEvent = async () => {
        if (!selectedCalendarId || !summary || !canvasEvent?.calendarEventId) return;

        setIsSaving(true);
        try {
            const formData = new FormData();
            formData.append("calendar_id", String(selectedCalendarId));
            formData.append("calendar_event_id", String(canvasEvent.calendarEventId));
            formData.append("summary", summary);
            formData.append("description", description);
            formData.append("location", location);
            formData.append("start", dayjs(canvasEvent.start).format("YYYY-MM-DDTHH:mm:ss"));
            formData.append("end", dayjs(canvasEvent.end).format("YYYY-MM-DDTHH:mm:ss"));
            formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
            formData.append("create_meeting", String(createMeeting));
            if (attendees.length > 0) {
                formData.append("attendees", JSON.stringify(attendees));
            }
            if (selectedColorId) {
                formData.append("color_id", String(selectedColorId));
            }

            const existingFiles = await Promise.all(
                existingAttachments.map(async (att) => {
                    const response = await fetch(att.attachment_url);
                    const blob = await response.blob();
                    return new File([blob], att.title, { type: blob.type || "application/octet-stream" });
                })
            );

            attachments.forEach((file) => {
                formData.append("attachments", file);
            });

            existingFiles.forEach((file) => {
                formData.append("attachments", file);
            });

            await CalendarService.updateEvent(formData);
            showSuccessAlert("Event updated successfully");
            fetchCalendarEvents();
            closeCanvas();
        } catch (error: any) {
            showErrorAlert(error.response?.data?.error || "Failed to update event");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteEvent = async () => {
        if (!selectedEvent?.linkedCalendarId || !selectedEvent?.calendarEventId) return;

        const result = await showDeleteConfirmationAlert("Delete Event", "Are you sure you want to delete this event?");
        if (result.isConfirmed) {
            setDetailLoading(true);
            try {
                await CalendarService.deleteEvent(selectedEvent.linkedCalendarId, selectedEvent.calendarEventId);
                showSuccessAlert("Event deleted successfully");
                fetchCalendarEvents();
                closeEventModal();
            } catch (error: any) {
                showErrorAlert(error.response?.data?.error || "Failed to delete event");
            } finally {
                setDetailLoading(false);
            }
        }
    };

    const handleEditEvent = (event: ICalendarItem) => {
        setCanvasEvent(event);
        setSummary(event.title);
        setDescription(event.description || "");
        setLocation(event.location || "");
        setSelectedCalendarId(event.linkedCalendarId || "");
        setAttachments([]);
        setExistingAttachments(event.attachments || []);
        setCreateMeeting(eventMeetings.length > 0);
        setAttendees(eventAttendees.map(att => att.attendee_email));
        setAttendeeInput("");
        const apiEvent = apiEvents.find(e => e.calendar_event_id === event.calendarEventId);
        setSelectedColorId(apiEvent?.calendar_color_category_id || "");
        setIsEditing(true);
        setToggleInfoEventCanvas(true);
        closeEventModal();
    };

    const toggleProviderExpanded = (providerKey: string) => {
        setExpandedProviders((prev) => ({ ...prev, [providerKey]: !prev[providerKey] }));
    };

    const toggleCalendarEnabled = (calId: number) => {
        setEnabledCalendars((prev) => ({ ...prev, [calId]: !prev[calId] }));
    };

    const toggleEventType = (typeId: number) => {
        setEnabledEventTypes((prev) => ({ ...prev, [typeId]: !prev[typeId] }));
    };

    const unitType = getUnitType(viewMode);

    const viewModeIcons: Record<string, string> = {
        month: "ti ti-calendar",
        week: "ti ti-calendar-event",
        work_week: "ti ti-calendar-stats",
        day: "ti ti-calendar-time",
    };

    const viewModeLabels: Record<string, string> = {
        month: "Month",
        week: "Week",
        work_week: "Work Week",
        day: "Day",
    };

    const closeEventModal = () => {
        setSelectedEvent(null);
        setEventMeetings([]);
        setEventAttendees([]);
    };

    const handleShowRelatedEvents = async () => {
        if (!selectedTenantId || !selectedEvent?.calendarEventId) return;
        setRelatedEventTitle(selectedEvent.title);
        setRelatedEventColor(selectedEvent.backgroundColor || "");
        setRelatedEventsLoading(true);
        setShowRelatedEventsModal(true);
        closeEventModal();
        try {
            const events = await CalendarService.fetchRelatedEvents(selectedTenantId, selectedEvent.calendarEventId);
            const sortedEvents = events.sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime());
            setRelatedEvents(sortedEvents);
        } catch {
            setRelatedEvents([]);
        } finally {
            setRelatedEventsLoading(false);
        }
    };

    const closeRelatedEventsModal = () => {
        setShowRelatedEventsModal(false);
        setRelatedEvents([]);
    };

    const navigateToEvent = (ev: CalendarEvent) => {
        setDate(new Date(ev.start_datetime));
        setViewMode("day");
        closeRelatedEventsModal();
        closeEventModal();
    };

    const closeTaskModal = () => {
        setSelectedTask(null);
    };

    const closeCanvas = () => {
        setToggleInfoEventCanvas(false);
        setCanvasEvent(null);
        setAttachments([]);
        setExistingAttachments([]);
        setCreateMeeting(false);
        setAttendees([]);
        setAttendeeInput("");
        setSelectedColorId("");
    };

    const handlePickerClick = (ref: React.RefObject<HTMLInputElement>) => {
        const input = ref.current;
        if (input) {
            try {
                if (typeof (input as any).showPicker === "function") {
                    (input as any).showPicker();
                } else {
                    input.focus();
                }
            } catch {
                input.focus();
            }
        }
    };

    const taskFullData = useMemo(() => {
        if (!selectedTask) return null;
        return apiTasks.find((t) => t.calendar_task_id === selectedTask.calendarTaskId) || null;
    }, [selectedTask, apiTasks]);

    const subTasks = useMemo(() => {
        if (!taskFullData) return [];
        return taskFullData.sub_tasks || [];
    }, [taskFullData]);

    const meetingLinks = useMemo(() => {
        return eventMeetings.filter(
            (m) => m.meeting_url && !m.meeting_url.startsWith("tel:")
        );
    }, [eventMeetings]);

    const phoneLinks = useMemo(() => {
        return eventMeetings.filter(
            (m) => m.meeting_url && m.meeting_url.startsWith("tel:")
        );
    }, [eventMeetings]);

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="page-header">
                    <div className="row align-items-center">
                        <div className="col-sm-12 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-4">
                                <h4 className="page-title">Calendar</h4>
                                <button
                                    className="btn btn-sm btn-outline-primary rounded-pill px-3 mb-2"
                                    onClick={() => setDate(dayjs().toDate())}
                                >
                                    Today
                                </button>
                            </div>
                            <div className="calendar-tasks-toggle mb-2">
                                <button className="toggle-btn active">
                                    <i className="ti ti-calendar" />
                                    Calendar
                                </button>
                                <button className="toggle-btn" onClick={() => navigate("/application/tasks")}>
                                    <i className="ti ti-checklist" />
                                    Tasks
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-xl-3">
                        <div className="card mb-3" style={{ borderRadius: "8px" }}>
                            <div className="card-body p-2">
                                <MiniCalendar selectedDate={date} onDateSelect={(d) => setDate(d)} />
                            </div>
                        </div>

                        <div className="card mb-3" style={{ borderRadius: "8px" }}>
                            <div className="card-header bg-white" style={{ borderBottom: "1px solid #eee" }}>
                                <h5 className="card-title mb-0" style={{ fontSize: "0.85rem" }}>Event Types</h5>
                            </div>
                            <div className="card-body py-2">
                                <div className="d-flex flex-column gap-2">
                                    {eventTypes.map((et) => (
                                        <div key={et.calendar_event_type_id} className="d-flex align-items-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input me-2"
                                                checked={!!enabledEventTypes[et.calendar_event_type_id]}
                                                onChange={() => toggleEventType(et.calendar_event_type_id)}
                                                id={`et-${et.calendar_event_type_id}`}
                                            />
                                            <label htmlFor={`et-${et.calendar_event_type_id}`} className="mb-0" style={{ cursor: "pointer", fontSize: "0.85rem" }}>
                                                {et.calendar_event_type_display_name}
                                            </label>
                                        </div>
                                    ))}
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input me-2"
                                            checked={tasksEnabled}
                                            onChange={() => setTasksEnabled(!tasksEnabled)}
                                            id="et-task"
                                        />
                                        <label htmlFor="et-task" className="mb-0" style={{ cursor: "pointer", fontSize: "0.85rem" }}>
                                            Task
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ borderRadius: "8px" }}>
                            <div className="card-header bg-white d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid #eee" }}>
                                <h5 className="card-title mb-0" style={{ fontSize: "0.85rem" }}>Calendars</h5>
                            </div>
                            <div className="card-body p-0">
                                {calendarsByProvider.map((group) => {
                                    const provConfig = PROVIDER_CONFIG[group.key] || { icon: "ti ti-calendar", iconColor: "#6366f1" };
                                    const isExpanded = !!expandedProviders[group.key];
                                    return (
                                        <div key={group.key} className="calendar-provider">
                                            <button
                                                className="calendar-provider-header"
                                                onClick={() => toggleProviderExpanded(group.key)}
                                            >
                                                <span className="d-flex align-items-center gap-2">
                                                    <i className={provConfig.icon} style={{ color: provConfig.iconColor, fontSize: "1rem" }} />
                                                    <span className="fw-semibold" style={{ fontSize: "0.82rem" }}>{group.displayName}</span>
                                                    <span className="badge bg-light text-muted" style={{ fontSize: "0.7rem" }}>{group.calendars.length}</span>
                                                </span>
                                                <i
                                                    className={classNames("ti", { "ti-chevron-down": isExpanded, "ti-chevron-right": !isExpanded })}
                                                    style={{ fontSize: "0.75rem", color: "#94a3b8" }}
                                                />
                                            </button>
                                            {isExpanded && (
                                                <div className="calendar-provider-accounts">
                                                    {group.calendars.map((cal) => (
                                                        <div key={cal.calendar_auth_linked_calendar_id} className="calendar-account-item">
                                                            <div className="d-flex align-items-center gap-2 flex-1" style={{ minWidth: 0 }}>
                                                                <div
                                                                    className="calendar-account-dot"
                                                                    style={{ backgroundColor: getModernColor(cal.calendar_color_category?.background_color_code || "#4285F4") }}
                                                                />
                                                                <label
                                                                    htmlFor={`cal-${cal.calendar_auth_linked_calendar_id}`}
                                                                    className="mb-0 text-truncate"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        fontSize: "0.8rem",
                                                                        color: enabledCalendars[cal.calendar_auth_linked_calendar_id] ? "#334155" : "#94a3b8",
                                                                    }}
                                                                    title={cal.name}
                                                                >
                                                                    {cal.name}
                                                                    {cal.is_primary && (
                                                                        <span className="ms-1" style={{ fontSize: "0.65rem", color: "#6366f1" }}>(Primary)</span>
                                                                    )}
                                                                </label>
                                                            </div>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input flex-shrink-0"
                                                                checked={!!enabledCalendars[cal.calendar_auth_linked_calendar_id]}
                                                                onChange={() => toggleCalendarEnabled(cal.calendar_auth_linked_calendar_id)}
                                                                id={`cal-${cal.calendar_auth_linked_calendar_id}`}
                                                                style={{ accentColor: getModernColor(cal.calendar_color_category?.background_color_code || "#4285F4") }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {calendarsByProvider.length === 0 && (
                                    <div className="p-3 text-center text-muted" style={{ fontSize: "0.82rem" }}>
                                        No calendars linked
                                        <br /><br />
                                        <button
                                            className="btn btn-sm btn-primary"
                                            style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: "6px" }}
                                            onClick={() => navigate(all_routes.calendarSettings)}
                                            title="Integrate Calendar"
                                        >
                                            <i className="ti ti-calendar-plus me-1" style={{ fontSize: "0.72rem" }} />
                                            Integrate Calendar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-9">
                        <div className="card" style={{ borderRadius: "8px" }}>
                            <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ borderBottom: "1px solid #eee" }}>
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        className="btn btn-white btn-icon btn-sm border-0"
                                        onClick={() => setDate(dayjs(date).add(-1, unitType).toDate())}
                                    >
                                        <i className="ti ti-chevron-left" />
                                    </button>
                                    <h5 className="card-title mb-0">{getLabel(date, viewMode)}</h5>
                                    <button
                                        className="btn btn-white btn-icon btn-sm border-0"
                                        onClick={() => setDate(dayjs(date).add(1, unitType).toDate())}
                                    >
                                        <i className="ti ti-chevron-right" />
                                    </button>
                                </div>
                                <div className="d-flex align-items-center gap-2">

                                    <div className="btn-group">
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => setDate(dayjs(date).add(-1, unitType).toDate())}
                                        >
                                            <i className="ti ti-chevron-left" />
                                        </button>

                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => setDate(dayjs().toDate())}
                                        >
                                            {getTodayButtonLabel(viewMode)}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => setDate(dayjs(date).add(1, unitType).toDate())}
                                        >
                                            <i className="ti ti-chevron-right" />
                                        </button>
                                    </div>

                                    <div className="dropdown view-mode-dropdown mx-2" ref={viewModeDropdownRef}>
                                        <button
                                            className="btn btn-sm btn-outline-primary dropdown-toggle"
                                            onClick={() => setViewModeDropdownOpen(!viewModeDropdownOpen)}
                                        >
                                            <i className={`${viewModeIcons[viewMode]} me-1`} />
                                            {viewModeLabels[viewMode]}
                                        </button>
                                        <ul
                                            className={classNames("dropdown-menu dropdown-menu-end", { show: viewModeDropdownOpen })}
                                            style={viewModeDropdownOpen ? { display: "block" } : {}}
                                        >
                                            {(["month", "week", "work_week", "day"] as ViewMode[]).map((mode) => (
                                                <li key={mode}>
                                                    <button
                                                        className={classNames("dropdown-item", { active: viewMode === mode })}
                                                        onClick={() => {
                                                            setViewMode(mode);
                                                            setViewModeDropdownOpen(false);
                                                        }}
                                                    >
                                                        <i className={classNames(`${viewModeIcons[mode]} me-2`, { "text-white": viewMode === mode })} />
                                                        <span className={classNames({ "text-white": viewMode === mode })}>{viewModeLabels[mode]}</span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body" style={{ position: "relative" }}>
                                {loading && (
                                    <div className="calendar-loading-overlay">
                                        <div className="spinner-border text-primary spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                )}
                                <BigCalendar
                                    selectable
                                    localizer={localizer}
                                    events={calendarItems}
                                    defaultView={viewMode}
                                    view={viewMode}
                                    views={["month", "week", "work_week", "day"]}
                                    onView={(v) => setViewMode(v as ViewMode)}
                                    onNavigate={(d) => setDate(d)}
                                    date={date}
                                    onSelectEvent={onSelectEvent}
                                    onSelectSlot={handleSlotSelect}
                                    eventPropGetter={eventStyleGetter}
                                    components={{
                                        event: EventComponent,
                                    }}
                                    style={{ height: 700 }}
                                    onDrillDown={handleViewMode}
                                    showMultiDayTimes={true}
                                    allDayAccessor="isAllDay"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {selectedEvent && (
                    <>
                        <div className="modal-backdrop fade show" onClick={closeEventModal} />
                        <div className="modal fade show d-block" tabIndex={-1} onClick={closeEventModal}>
                            <div className="modal-dialog modal-dialog-centered modal-w-xl" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                    <div
                                        className="modal-header"
                                        style={{
                                            background: selectedEvent.backgroundColor
                                                ? `linear-gradient(135deg, ${selectedEvent.backgroundColor} 0%, ${adjustColor(selectedEvent.backgroundColor, -30)} 100%)`
                                                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            color: "white",
                                        }}
                                    >
                                        <h5 className="modal-title" style={{ color: "white" }}>
                                            <i className="ti ti-calendar-event me-2" />
                                            {selectedEvent.title}
                                            {selectedEvent.relatedEntityName === "task" && (
                                                <>

                                                    {selectedEvent.relatedEntityId && (
                                                        <>
                                                            <span className="badge ms-2" style={{ fontSize: "0.65rem", backgroundColor: "rgba(255,255,255,0.2)", color: "#fff" }}>
                                                                Created from Task
                                                                <i
                                                                    className="ti ti-external-link ms-2"
                                                                    style={{ fontSize: "1rem", color: "#fff", cursor: "pointer" }}
                                                                    onClick={() => {
                                                                        closeEventModal();
                                                                        handleOpenCRMTask(selectedEvent.relatedEntityId as number);
                                                                    }}
                                                                    title="Open Related Task Details"
                                                                />
                                                            </span>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </h5>
                                        <button type="button" className="btn-close btn-close-white" onClick={closeEventModal}>
                                            <i className="fa fa-close" />
                                        </button>
                                    </div>
                                    <div className="modal-body p-4">
                                        {detailLoading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mt-2 text-muted">Loading event details...</p>
                                            </div>
                                        ) : (
                                            <div className="row g-4">
                                                <div className={eventMeetings.length > 0 || eventAttendees.length > 0 || selectedEvent.description || (selectedEvent.attachments && selectedEvent.attachments.length > 0) ? "col-md-6" : "col-12"}>
                                                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "10px" }}>
                                                        <div className="card-body">
                                                            <h6 className="text-muted text-uppercase fw-bold mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                                                Event Details
                                                            </h6>
                                                            <div className="mb-3 d-flex align-items-start">
                                                                <i className="ti ti-clock me-3 text-primary" style={{ fontSize: "1.2rem", marginTop: "2px" }} />
                                                                <div>
                                                                    <div className="fw-medium">Start Time</div>
                                                                    <div className="text-muted">{formatCustomDateTime(selectedEvent.start.toISOString())}</div>
                                                                </div>
                                                            </div>
                                                            <div className="mb-3 d-flex align-items-start">
                                                                <i className="ti ti-clock-off me-3 text-danger" style={{ fontSize: "1.2rem", marginTop: "2px" }} />
                                                                <div>
                                                                    <div className="fw-medium">End Time</div>
                                                                    <div className="text-muted">{formatCustomDateTime(selectedEvent.end.toISOString())}</div>
                                                                </div>
                                                            </div>
                                                            {selectedEvent.timezone && (
                                                                <div className="mb-3 d-flex align-items-start">
                                                                    <i className="fa fa-globe me-3" style={{ fontSize: "1.2rem", marginTop: "2px", color: "#6366f1" }} />
                                                                    <div>
                                                                        <div className="fw-medium">Timezone</div>
                                                                        <div className="text-muted">{selectedEvent.timezone}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {selectedEvent.location && (
                                                                <div className="mb-3 d-flex align-items-start">
                                                                    <i className="ti ti-map-pin me-3" style={{ fontSize: "1.2rem", marginTop: "2px", color: "#f43f5e" }} />
                                                                    <div>
                                                                        <div className="fw-medium">Location</div>
                                                                        <div className="text-muted">{selectedEvent.location}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {selectedEvent.status && (
                                                                <div className="mb-3 d-flex align-items-start">
                                                                    <i className="ti ti-circle-check me-3" style={{ fontSize: "1.2rem", marginTop: "2px", color: "#22c55e" }} />
                                                                    <div>
                                                                        <div className="fw-medium">Status</div>
                                                                        <div className="text-muted text-capitalize">{selectedEvent.status}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {selectedEvent.calendarName && (
                                                                <div className="mb-3 d-flex align-items-start">
                                                                    <i className="ti ti-calendar me-3" style={{ fontSize: "1.2rem", marginTop: "2px", color: "#06b6d4" }} />
                                                                    <div>
                                                                        <div className="fw-medium">Calendar</div>
                                                                        <div className="text-muted">{selectedEvent.calendarName}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {selectedEvent.description && (
                                                                <div className="d-flex align-items-start">
                                                                    <i className="ti ti-file-description me-3" style={{ fontSize: "1.2rem", marginTop: "2px", color: "#06b6d4" }} />
                                                                    <div>
                                                                        <div className="fw-medium">Description</div>
                                                                        <div className="text-muted">{selectedEvent.description}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {(eventMeetings.length > 0 || eventAttendees.length > 0 || selectedEvent.description || (selectedEvent.attachments && selectedEvent.attachments.length > 0)) && (
                                                    <div className="col-md-6">
                                                        {selectedEvent.attachments && selectedEvent.attachments.length > 0 && (
                                                            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "10px" }}>
                                                                <div className="card-body">
                                                                    <h6 className="text-muted text-uppercase fw-bold mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                                                        Attachments ({selectedEvent.attachments.length})
                                                                    </h6>
                                                                    <div className="d-flex flex-column gap-2">
                                                                        {selectedEvent.attachments.map((att) => (
                                                                            <a
                                                                                key={att.attachment_id}
                                                                                href={att.attachment_url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="d-flex align-items-center gap-2 p-2 rounded bg-light text-decoration-none"
                                                                                title={att.title}
                                                                                style={{ border: "1px solid #f1f5f9" }}
                                                                            >
                                                                                <i className="ti ti-paperclip text-primary" style={{ fontSize: "1rem" }} />
                                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                                    <div className="text-dark text-truncate fw-medium" style={{ fontSize: "0.8rem" }}>{att.title}</div>
                                                                                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>{att.size_in_kb} KB</div>
                                                                                </div>
                                                                            </a>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {meetingLinks.length > 0 && (
                                                            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "10px" }}>
                                                                <div className="card-body">
                                                                    <h6 className="text-muted text-uppercase fw-bold mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                                                        Meeting
                                                                    </h6>
                                                                    {meetingLinks.map((m) => (
                                                                        <div key={m.calendar_event_meeting_id} className="meeting-link-item mb-2">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <i
                                                                                    className={getMeetingIcon(m.meeting_provider)}
                                                                                    style={{ fontSize: "1.3rem", color: getMeetingIconColor(m.meeting_provider) }}
                                                                                />
                                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                                    <div className="fw-medium" style={{ fontSize: "0.85rem" }}>{m.meeting_provider}</div>
                                                                                    <a
                                                                                        href={m.meeting_url}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="text-truncate d-block"
                                                                                        style={{ fontSize: "0.78rem" }}
                                                                                    >
                                                                                        Join Meeting
                                                                                    </a>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                    {phoneLinks.length > 0 && (
                                                                        <div className="mt-2 pt-2" style={{ borderTop: "1px solid #f1f5f9" }}>
                                                                            {phoneLinks.map((m) => (
                                                                                <div key={m.calendar_event_meeting_id} className="d-flex align-items-center gap-2 mb-1">
                                                                                    <i className="ti ti-phone" style={{ fontSize: "1rem", color: "#22c55e" }} />
                                                                                    <a href={m.meeting_url} style={{ fontSize: "0.78rem" }}>
                                                                                        {m.meeting_url.replace("tel:", "")}
                                                                                    </a>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {eventAttendees.length > 0 && (
                                                            <div className="card border-0 shadow-sm" style={{ borderRadius: "10px" }}>
                                                                <div className="card-body">
                                                                    <h6 className="text-muted text-uppercase fw-bold mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                                                        Attendees ({eventAttendees.length})
                                                                    </h6>
                                                                    <div className="d-flex flex-column gap-2">
                                                                        {eventAttendees.map((att) => {
                                                                            const statusInfo = getStatusBadge(att.attendee_status);
                                                                            const displayName = att.attendee_name || att.attendee_email.split("@")[0];
                                                                            return (
                                                                                <div key={att.calendar_event_attendee_id} className="attendee-item d-flex align-items-center gap-2">
                                                                                    <UserAvatar name={displayName} size={32} />
                                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                                        <div className="d-flex align-items-center gap-1">
                                                                                            <span className="fw-medium text-truncate" style={{ fontSize: "0.83rem" }}>{displayName}</span>
                                                                                            {att.is_organizer && (
                                                                                                <span className="badge" style={{ fontSize: "0.6rem", backgroundColor: "rgba(99,102,241,0.1)", color: "#6366f1" }}>
                                                                                                    Organizer
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="text-muted text-truncate" style={{ fontSize: "0.75rem" }}>{att.attendee_email}</div>
                                                                                    </div>
                                                                                    <span
                                                                                        className="badge"
                                                                                        style={{ fontSize: "0.65rem", backgroundColor: statusInfo.bg, color: statusInfo.color }}
                                                                                    >
                                                                                        {statusInfo.label}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-footer border-0 d-flex justify-content-between">
                                        <div className="d-flex gap-2">
                                            {selectedEvent.htmlLink && (
                                                <a
                                                    href={selectedEvent.htmlLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <i className="ti ti-external-link me-1" />
                                                    Open in Calendar
                                                </a>
                                            )}
                                            {selectedEvent.isRecursive && (
                                                <button className="btn btn-sm btn-outline-primary" onClick={handleShowRelatedEvents}>
                                                    <i className="ti ti-refresh me-1" />
                                                    Show all recursive events
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditEvent(selectedEvent)}>
                                                <i className="ti ti-edit me-1" />
                                                Edit
                                            </button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={handleDeleteEvent}>
                                                <i className="ti ti-trash me-1" />
                                                Delete
                                            </button>
                                        </div>
                                        <button className="btn btn-light" onClick={closeEventModal}>Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {selectedTask && taskFullData && (
                    <>
                        <div className="modal-backdrop fade show" onClick={closeTaskModal} />
                        <div className="modal fade show d-block" tabIndex={-1} onClick={closeTaskModal}>
                            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }} onClick={(e) => e.stopPropagation()}>
                                <div className="modal-content" style={{ borderRadius: "16px", overflow: "hidden", border: "none", boxShadow: "0 25px 60px rgba(0,0,0,0.3)" }}>
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)",
                                            padding: "28px 28px 24px",
                                            position: "relative",
                                        }}
                                    >
                                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "url('data:image/svg+xml,...') repeat", opacity: 0.05 }} />
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            onClick={closeTaskModal}
                                            style={{ position: "absolute", top: "16px", right: "16px", opacity: 0.8 }}
                                        />
                                        <div className="d-flex align-items-center gap-3 mb-3" style={{ position: "relative" }}>
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: "12px",
                                                    backgroundColor: "rgba(255,255,255,0.2)",
                                                    backdropFilter: "blur(10px)",
                                                }}
                                            >
                                                <i className={classNames("ti", taskFullData.is_completed ? "ti-circle-check" : "ti-checkbox")} style={{ fontSize: "1.4rem", color: "#fff" }} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    {taskFullData.task_source === "calendar" && (
                                                        <span style={{ fontSize: "0.65rem", fontWeight: "bold", background: "rgba(255,255,255,0.2)", color: "#fff", padding: "2px 5px", borderRadius: "4px", lineHeight: 1 }}>Google Tasks</span>
                                                    )}
                                                    {taskFullData.task_source === "crm" && (
                                                        <span style={{ fontSize: "0.65rem", fontWeight: "bold", background: "rgba(255,255,255,0.2)", color: "#fff", padding: "2px 5px", borderRadius: "4px", lineHeight: 1 }}>CRM</span>
                                                    )}
                                                    <h5 className="mb-0 text-white fw-bold" style={{ fontSize: "1.1rem", lineHeight: 1.3 }}>
                                                        {taskFullData.title}
                                                    </h5>
                                                    {taskFullData.task_source === "crm" && taskFullData.related_task_id && (
                                                        <i
                                                            className="ti ti-external-link ms-2"
                                                            style={{ fontSize: "1rem", color: "#fff", cursor: "pointer" }}
                                                            onClick={() => handleOpenCRMTask(taskFullData.related_task_id as number)}
                                                            title="Open CRM Task Details"
                                                        />
                                                    )}
                                                </div>
                                                {taskFullData.calendar_task_group?.task_group_name && (
                                                    <span
                                                        className="d-inline-flex align-items-center gap-1"
                                                        style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)" }}
                                                    >
                                                        <i className="ti ti-folder" style={{ fontSize: "0.8rem" }} />
                                                        {taskFullData.calendar_task_group.task_group_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2" style={{ position: "relative" }}>
                                            <span
                                                className="d-inline-flex align-items-center gap-1"
                                                style={{
                                                    padding: "4px 12px",
                                                    borderRadius: "20px",
                                                    fontSize: "0.75rem",
                                                    fontWeight: 600,
                                                    backgroundColor: taskFullData.is_completed ? "rgba(34,197,94,0.25)" : "rgba(255,255,255,0.2)",
                                                    color: "#fff",
                                                    backdropFilter: "blur(10px)",
                                                }}
                                            >
                                                <i className={classNames("bi", taskFullData.is_completed ? "bi-check-circle" : "bi-arrow-repeat")} style={{ fontSize: "0.85rem" }} />
                                                {taskFullData.is_completed ? "Completed" : taskFullData.status === "needsAction" ? "Needs Action" : taskFullData.status}
                                            </span>
                                            <span
                                                className="d-inline-flex align-items-center gap-1"
                                                style={{
                                                    padding: "4px 12px",
                                                    borderRadius: "20px",
                                                    fontSize: "0.75rem",
                                                    fontWeight: 600,
                                                    backgroundColor: "rgba(255,255,255,0.15)",
                                                    color: "#fff",
                                                    backdropFilter: "blur(10px)",
                                                }}
                                            >
                                                <i
                                                    className="ti ti-flag-filled"
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        color: taskFullData.priority === "high" ? "#fca5a5" : taskFullData.priority === "medium" ? "#fcd34d" : "rgba(255,255,255,0.6)",
                                                    }}
                                                />
                                                <span className="text-capitalize">{taskFullData.priority}</span>
                                            </span>
                                            {!taskFullData.calendar_task_group_id && (
                                                <span
                                                    className="d-inline-flex align-items-center gap-1"
                                                    style={{
                                                        padding: "4px 12px",
                                                        borderRadius: "20px",
                                                        fontSize: "0.75rem",
                                                        fontWeight: 600,
                                                        backgroundColor: "rgba(245,158,11,0.25)",
                                                        color: "#fff",
                                                        backdropFilter: "blur(10px)",
                                                        border: "1px solid rgba(255,255,255,0.2)"
                                                    }}
                                                >
                                                    <i className="ti ti-alert-circle" style={{ fontSize: "0.85rem" }} />
                                                    Not Synced
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="modal-body p-0" style={{ backgroundColor: "#fafbfc" }}>
                                        <div style={{ padding: "20px 28px" }}>
                                            {taskFullData.due_datetime && (
                                                <div className="d-flex align-items-center gap-3 mb-3 p-3" style={{ backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e8edf2" }}>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "#eef2ff" }}
                                                    >
                                                        <i className="ti ti-calendar-due" style={{ fontSize: "1.1rem", color: "#4f46e5" }} />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px" }}>Due Date</div>
                                                        <div className="fw-semibold" style={{ fontSize: "0.88rem", color: "#1e293b" }}>{formatCustomDateTime(taskFullData.due_datetime)}</div>
                                                    </div>
                                                </div>
                                            )}

                                            {taskFullData.notes && (
                                                <div className="mb-3 p-3" style={{ backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e8edf2" }}>
                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                        <i className="ti ti-notes" style={{ fontSize: "0.95rem", color: "#64748b" }} />
                                                        <span style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px" }}>Notes</span>
                                                    </div>
                                                    <div style={{ fontSize: "0.85rem", color: "#475569", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                                                        {taskFullData.notes}
                                                    </div>
                                                </div>
                                            )}

                                            {subTasks.length > 0 && (
                                                <div className="p-3" style={{ backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e8edf2" }}>
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="ti ti-subtask" style={{ fontSize: "0.95rem", color: "#64748b" }} />
                                                            <span style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px" }}>Sub-tasks</span>
                                                        </div>
                                                        <span className="badge" style={{ fontSize: "0.7rem", backgroundColor: "#eef2ff", color: "#4f46e5", fontWeight: 600 }}>
                                                            {subTasks.filter((s) => s.is_completed).length}/{subTasks.length}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex flex-column gap-1">
                                                        {subTasks.map((st) => (
                                                            <div
                                                                key={st.calendar_task_id}
                                                                className="d-flex align-items-center gap-2 p-2"
                                                                style={{
                                                                    borderRadius: "8px",
                                                                    backgroundColor: st.is_completed ? "#f0fdf4" : "#fafbfc",
                                                                    transition: "background 0.15s",
                                                                }}
                                                            >
                                                                <i
                                                                    className={classNames("ti", st.is_completed ? "ti-circle-check" : "ti-circle")}
                                                                    style={{ color: st.is_completed ? "#22c55e" : "#cbd5e1", fontSize: "1.1rem" }}
                                                                />
                                                                <span
                                                                    className={classNames({ "text-decoration-line-through": st.is_completed })}
                                                                    style={{ fontSize: "0.84rem", color: st.is_completed ? "#94a3b8" : "#334155" }}
                                                                >
                                                                    {st.title}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ padding: "12px 28px 16px", borderTop: "1px solid #e8edf2" }}>
                                            <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.78rem", color: "#94a3b8" }}>
                                                <UserAvatar name={`${taskFullData.created_by?.first_name || ""} ${taskFullData.created_by?.last_name || ""}`} size={22} />
                                                <span>{taskFullData.created_by?.first_name} {taskFullData.created_by?.last_name}</span>
                                                <span style={{ color: "#d1d5db" }}>·</span>
                                                <span>{dayjs(taskFullData.created_datetime).format("MMM D, YYYY")}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0" style={{ padding: "12px 28px 20px", backgroundColor: "#fafbfc" }}>
                                        <button className="btn btn-light px-4" style={{ borderRadius: "8px" }} onClick={closeTaskModal}>Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {toggleInfoEventCanvas && (
                    <>
                        <div
                            className="offcanvas-backdrop fade show"
                            onClick={closeCanvas}
                        />
                        <div
                            className={classNames("offcanvas offcanvas-end show", "calendar-offcanvas")}
                            tabIndex={-1}
                            style={{ visibility: "visible", width: "800px" }}
                        >
                            <div className="offcanvas-header p-4" style={{ background: "#824EEB" }}>
                                <h5 className="offcanvas-title" style={{ color: "#fff" }}>
                                    <i className={classNames("ti me-2", isEditing ? "ti-calendar-edit" : "ti-calendar-plus")} style={{ color: "#fff" }} />
                                    {isEditing ? "Edit Event" : "Create Event"}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={closeCanvas} />
                            </div>
                            <div className="offcanvas-body p-4">
                                {canvasEvent && (
                                    <div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Connected Calendar <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                value={selectedCalendarId}
                                                onChange={(e) => setSelectedCalendarId(e.target.value)}
                                            >
                                                <option value="">Select Calendar</option>
                                                {linkedCalendars.map((cal) => (
                                                    <option key={cal.calendar_auth_linked_calendar_id} value={cal.calendar_auth_linked_calendar_id}>
                                                        {cal.name} ({cal.calendar_provider?.calendar_provider_display_name})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Event Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter event name"
                                                value={summary}
                                                onChange={(e) => setSummary(e.target.value)}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Event Color</label>
                                            <div className="d-flex flex-wrap gap-2">
                                                <div
                                                    className={classNames("color-swatch", { active: !selectedColorId })}
                                                    style={{
                                                        backgroundColor: getModernColor("#039BE5"),
                                                        width: "28px",
                                                        height: "28px",
                                                        borderRadius: "50%",
                                                        cursor: "pointer",
                                                        border: !selectedColorId ? "2px solid #000" : "1px solid #ddd",
                                                        transition: "transform 0.2s",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                    onClick={() => setSelectedColorId("")}
                                                    title="Default Color"
                                                >
                                                    {!selectedColorId && (
                                                        <i className="ti ti-check" style={{ color: getContrastColor(getModernColor("#039BE5")), fontSize: "0.8rem" }} />
                                                    )}
                                                </div>
                                                {MODERN_PALETTE.map((modernHex) => {
                                                    const cc = colorCategories.find(c => getModernColor(c.background_color_code).toLowerCase() === modernHex.toLowerCase());
                                                    if (!cc) return null;
                                                    return (
                                                        <div
                                                            key={cc.calendar_color_category_id}
                                                            className={classNames("color-swatch", { active: String(selectedColorId) === String(cc.calendar_color_category_id) })}
                                                            style={{
                                                                backgroundColor: modernHex,
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "50%",
                                                                cursor: "pointer",
                                                                border: String(selectedColorId) === String(cc.calendar_color_category_id) ? "2px solid #000" : "1px solid #ddd",
                                                                transition: "transform 0.2s",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center"
                                                            }}
                                                            onClick={() => setSelectedColorId(cc.calendar_color_category_id)}
                                                            title={modernHex}
                                                        >
                                                            {String(selectedColorId) === String(cc.calendar_color_category_id) && (
                                                                <i className="ti ti-check" style={{ color: getContrastColor(modernHex), fontSize: "0.8rem" }} />
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Location</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter location"
                                                value={location}
                                                onChange={(e) => setLocation(e.target.value)}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Description</label>
                                            <textarea
                                                className="form-control"
                                                rows={3}
                                                placeholder="Enter description"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Attendees (Emails)</label>
                                            <CreatableSelect
                                                isMulti
                                                components={{ DropdownIndicator: null, IndicatorSeparator: null, Menu: () => null }}
                                                placeholder="Type email and press enter..."
                                                inputValue={attendeeInput}
                                                onInputChange={(val) => setAttendeeInput(val)}
                                                onKeyDown={(e: any) => {
                                                    if (!attendeeInput) return;
                                                    if (e.key === "Enter" || e.key === "Tab") {
                                                        e.preventDefault();
                                                        if (!attendees.includes(attendeeInput)) {
                                                            setAttendees([...attendees, attendeeInput]);
                                                        }
                                                        setAttendeeInput("");
                                                    }
                                                }}
                                                value={attendees.map(email => ({ label: email, value: email }))}
                                                onChange={(newValue: any) => {
                                                    const emails = (newValue || []).map((opt: any) => opt.value);
                                                    setAttendees(emails);
                                                }}
                                                classNamePrefix="react-select"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        borderRadius: "8px",
                                                        borderColor: "#ced4da",
                                                        minHeight: "42px",
                                                        boxShadow: "none",
                                                        "&:hover": {
                                                            borderColor: "#ced4da"
                                                        }
                                                    }),
                                                    multiValue: (base) => ({
                                                        ...base,
                                                        backgroundColor: "#824EEB",
                                                        borderRadius: "4px",
                                                    }),
                                                    multiValueLabel: (base) => ({
                                                        ...base,
                                                        color: "#fff",
                                                    }),
                                                    multiValueRemove: (base) => ({
                                                        ...base,
                                                        color: "#fff",
                                                        ":hover": {
                                                            backgroundColor: "#6b3dc4",
                                                            color: "white",
                                                        },
                                                    }),
                                                }}
                                            />
                                        </div>
                                        <div className="mb-3 d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input mt-0"
                                                id="create_meeting"
                                                checked={createMeeting}
                                                onChange={(e) => setCreateMeeting(e.target.checked)}
                                            />
                                            <label className="form-label fw-semibold mb-0" htmlFor="create_meeting">Create Meeting</label>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Attachments</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                multiple
                                                onChange={(e) => {
                                                    if (e.target.files) {
                                                        const newFiles = Array.from(e.target.files);
                                                        setAttachments((prev) => [...prev, ...newFiles]);
                                                    }
                                                }}
                                            />
                                            {attachments.length > 0 && (
                                                <div className="mt-2 d-flex flex-wrap gap-2">
                                                    {attachments.map((file, index) => (
                                                        <div key={index} className="badge bg-light text-dark border d-flex align-items-center gap-2 p-2" style={{ borderRadius: "6px" }}>
                                                            <i className="ti ti-file" style={{ fontSize: "0.9rem" }} />
                                                            <span className="text-truncate" style={{ maxWidth: "150px", fontSize: "0.75rem" }}>{file.name}</span>
                                                            <button
                                                                type="button"
                                                                className="btn-close"
                                                                style={{ fontSize: "0.5rem", padding: "0.25rem" }}
                                                                onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {existingAttachments.length > 0 && (
                                                <div className="mt-2 d-flex flex-wrap gap-2">
                                                    {existingAttachments.map((att) => (
                                                        <div key={att.attachment_id} className="badge bg-soft-primary text-primary border d-flex align-items-center gap-2 p-2" style={{ borderRadius: "6px" }}>
                                                            <i className="ti ti-paperclip" style={{ fontSize: "0.9rem" }} />
                                                            <span className="text-truncate" style={{ maxWidth: "150px", fontSize: "0.75rem" }}>{att.title}</span>

                                                            <button
                                                                type="button"
                                                                className="btn-close"
                                                                style={{ fontSize: "0.5rem", padding: "0.25rem" }}
                                                                onClick={() => setExistingAttachments((prev) => prev.filter((a) => a.attachment_id !== att.attachment_id))}
                                                            />

                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Start Date & Time</label>
                                            <div className="datetime-picker-wrapper" onClick={() => handlePickerClick(startInputRef)}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formatCustomDateTime(canvasEvent.start.toISOString())}
                                                    readOnly
                                                />
                                                <i className="ti ti-calendar-event datetime-picker-icon" style={{ color: "#824EEB" }} />
                                                <input
                                                    ref={startInputRef}
                                                    type="datetime-local"
                                                    className="hidden-native-picker"
                                                    value={dayjs(canvasEvent.start).format("YYYY-MM-DDTHH:mm")}
                                                    onChange={(e) => {
                                                        if (canvasEvent) {
                                                            setCanvasEvent({ ...canvasEvent, start: new Date(e.target.value) });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">End Date & Time</label>
                                            <div className="datetime-picker-wrapper" onClick={() => handlePickerClick(endInputRef)}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formatCustomDateTime(canvasEvent.end.toISOString())}
                                                    readOnly
                                                />
                                                <i className="ti ti-calendar-event datetime-picker-icon" style={{ color: "#824EEB" }} />
                                                <input
                                                    ref={endInputRef}
                                                    type="datetime-local"
                                                    className="hidden-native-picker"
                                                    value={dayjs(canvasEvent.end).format("YYYY-MM-DDTHH:mm")}
                                                    onChange={(e) => {
                                                        if (canvasEvent) {
                                                            setCanvasEvent({ ...canvasEvent, end: new Date(e.target.value) });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-grid gap-2 mt-4">
                                            <button
                                                className="btn btn-primary"
                                                onClick={isEditing ? handleUpdateEvent : handleCreateEvent}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? (
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                ) : (
                                                    <i className="ti ti-check me-2" />
                                                )}
                                                {isEditing ? "Update Event" : "Save Event"}
                                            </button>
                                            <button className="btn btn-light" onClick={closeCanvas} disabled={isSaving}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
                {showRelatedEventsModal && (
                    <>
                        <div className="modal-backdrop fade show" onClick={closeRelatedEventsModal} style={{ zIndex: 1060 }} />
                        <div className="modal fade show d-block" tabIndex={-1} onClick={closeRelatedEventsModal} style={{ zIndex: 1061 }}>
                            <div className="modal-dialog modal-dialog-centered modal-w-xl" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                    <div
                                        className="modal-header"
                                        style={{
                                            background: relatedEventColor
                                                ? `linear-gradient(135deg, ${relatedEventColor} 0%, ${adjustColor(relatedEventColor, -30)} 100%)`
                                                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            color: "white",
                                        }}
                                    >
                                        <h5 className="modal-title text-white">
                                            <i className="ti ti-refresh me-2" />
                                            {relatedEventTitle}
                                        </h5>
                                        <button type="button" className="btn-close btn-close-white" onClick={closeRelatedEventsModal} />
                                    </div>
                                    <div className="modal-body p-4" style={{ maxHeight: "70vh", overflowY: "auto", backgroundColor: "#f8f9fa" }}>
                                        {relatedEventsLoading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mt-2 text-muted">Fetching recurring events...</p>
                                            </div>
                                        ) : relatedEvents.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ti ti-calendar-off text-muted mb-3" style={{ fontSize: "3rem" }} />
                                                <p className="text-muted">No recurring events found.</p>
                                            </div>
                                        ) : (
                                            <div className="row g-3">
                                                {relatedEvents.map((ev) => (
                                                    <div key={ev.calendar_event_id} className="col-md-6">
                                                        <div
                                                            className="card h-100 border-0 shadow-sm event-related-card"
                                                            style={{ borderRadius: "10px", cursor: "pointer", transition: "transform 0.2s" }}
                                                            onClick={() => navigateToEvent(ev)}
                                                        >
                                                            <div className="card-body p-3">
                                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                                    <div
                                                                        style={{
                                                                            width: "12px",
                                                                            height: "12px",
                                                                            borderRadius: "50%",
                                                                            backgroundColor: getModernColor(ev.calendar_color_category?.background_color_code || "#039BE5")
                                                                        }}
                                                                    />
                                                                    <h6 className="mb-0 text-truncate fw-bold" style={{ fontSize: "0.9rem" }}>{ev.title}</h6>
                                                                </div>
                                                                <div className="d-flex align-items-start gap-2 mb-1">
                                                                    <i className="ti ti-clock text-muted" style={{ fontSize: "0.85rem", marginTop: "3px" }} />
                                                                    <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                                                                        {dayjs(ev.start_datetime).format("MMM D, YYYY")}
                                                                        <br />
                                                                        {dayjs(ev.start_datetime).format("h:mm A")} - {dayjs(ev.end_datetime).format("h:mm A")}
                                                                    </div>
                                                                </div>
                                                                {ev.location && (
                                                                    <div className="d-flex align-items-start gap-2">
                                                                        <i className="ti ti-map-pin text-muted" style={{ fontSize: "0.85rem", marginTop: "3px" }} />
                                                                        <div className="text-truncate text-muted" style={{ fontSize: "0.75rem" }}>{ev.location}</div>
                                                                    </div>
                                                                )}
                                                                <div className="mt-3 text-end">
                                                                    <span className="text-primary" style={{ fontSize: "0.75rem", fontWeight: 600 }}>
                                                                        View in Calendar <i className="ti ti-chevron-right ms-1" />
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button className="btn btn-light" onClick={closeRelatedEventsModal}>Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <TaskDetailModal
                isVisible={isTaskModalVisible}
                taskDetails={taskDetails}
                isLoading={isLoadingTaskDetails}
                onClose={() => setIsTaskModalVisible(false)}
                onTaskUpdate={() => { linkedCalendars.length > 0 ? fetchTasks() : fetchCrmTasks(); }}
                tenantId={selectedTenantId}
            />
        </div>
    );
};

function adjustColor(hex: string, amount: number): string {
    let color = hex.replace("#", "");
    if (color.length === 3) {
        color = color
            .split("")
            .map((c) => c + c)
            .join("");
    }
    const num = parseInt(color, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00ff) + amount;
    let b = (num & 0x0000ff) + amount;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function getContrastColor(hexColor: string): string {
    let hex = hexColor.replace("#", "");
    if (hex.length === 3) {
        hex = hex.split("").map(c => c + c).join("");
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? "#000000" : "#FFFFFF";
}

export default CalendarPage;
